// src/features/scheduleGenerator/hooks/useScheduleGenerator.ts
import { useState, useRef, useEffect, useCallback } from 'react';
import { parse, parseISO } from 'date-fns';
import { TimeBox } from '../../../types/timeBox';
import { Event } from '../../../types/calendar-event';
import { ScheduleItem } from '../../../types/scheduleItem';
import {
  generateDailyPlan,
  syncDailySchedule,
  generateToneOptions as apiGenerateToneOptions,
  generateSkeleton as apiGenerateSkeleton,
  recommendTasks as apiRecommendTasks,
  assignTasksToTimeBoxes as apiAssignTasksToTimeBoxes,
  ToneOption,
  SkeletonTimeBlock,
  SkeletonEvent,
  SkeletonAlldayEvent,
  TaskRecommendation,
  TaskAssignmentTimeBox,
} from '../services/scheduleGeneratorService';
import { PlanningSessionManager } from '../utils/planningSessionManager';
import { useToast } from '../../../contexts/ToastContext';
import { useSubscription } from '../../../hooks/useSubscription';
import { useAuth } from '../../../contexts/AuthContext';
import { logger } from '../../../utils/logger';
import { getTimeBoxesByDate, getEventsByDateLocal } from '../../../services/indexedDB';
import { formatDateToString, toDateObject } from '../../../utils/dateUtils';

// ============================================================
// Day Draft V3 狀態機
// ============================================================

export type DayDraftStep =
  | 'IDLE'
  | 'TONE_LOADING'
  | 'TONE_READY'
  | 'SKELETON_GENERATING'
  | 'SKELETON_READY'
  | 'TASK_RECOMMENDING'
  | 'TASK_READY'
  | 'TASK_ASSIGNING'
  | 'APPLYING';

export interface ToneSelectionState {
  summary: string;
  options: ToneOption[];
}

export interface SkeletonState {
  timeBlocks: SkeletonTimeBlock[];
  intention: string;
  timedEvents: SkeletonEvent[];
  alldayEvents: SkeletonAlldayEvent[];
  rawTimedEvents: Event[];
  instructionSummary?: string;
}

export interface TaskRecommendState {
  recommendations: TaskRecommendation[];
  tasks: Array<{ id: string; title: string; category?: string; estimatedTime?: number }>;
}

export interface TaskAssignmentState {
  timeBoxes: TaskAssignmentTimeBox[];
}

export interface ScheduleGeneratorHookContext {
  source: 'today-current' | 'today-tomorrow' | 'calendar';
  date: string;
  onSuccess?: () => void;
}

export interface UseScheduleGeneratorReturn {
  // 狀態（舊版，保留向後相容）
  isAIPlanSettingsModalOpen: boolean;
  isAIPlanPreviewModalOpen: boolean;
  isGeneratingPlan: boolean;
  isApplyingPlan: boolean;
  planError: string | null;
  currentPlan: {
    date: string;
    timeBoxes: TimeBox[];
    events?: Event[];
    scheduleItems?: ScheduleItem[];
    summary?: string;
    pastScheduleItems?: TimeBox[];
  } | null;

  // Modal 控制（舊版）
  openSettings: () => void;
  closeSettings: () => void;
  closePreview: () => void;

  // 主要功能（舊版）
  generatePlan: (date: string, latestNote: string) => Promise<void>;
  regeneratePlan: (adjustmentText?: string) => Promise<void>;
  applyPlan: (scheduleItems: ScheduleItem[], onSuccess?: () => void) => Promise<void>;
  cancelGeneration: () => void;

  // Day Draft V3 狀態機
  draftStep: DayDraftStep;
  draftDate: string | null;
  toneState: ToneSelectionState | null;
  skeletonState: SkeletonState | null;
  taskState: TaskRecommendState | null;
  assignState: TaskAssignmentState | null;
  draftError: string | null;

  // Day Draft V3 操作
  startDraft: (date: string) => Promise<void>;
  selectTone: (intention: string) => Promise<void>;
  finishSkeleton: (onSuccess?: () => void) => Promise<void>;
  startDraftTaskRecommend: () => Promise<void>;
  assignTasks: (selectedTaskIds: string[]) => Promise<void>;
  applyDraft: (onSuccess?: () => void) => Promise<void>;
  goBackToTone: () => void;
  goBackToSkeleton: () => void;
  cancelDraft: () => void;
  fineTuneSkeleton: (instruction: string) => Promise<void>;
}

export const useScheduleGenerator = (): UseScheduleGeneratorReturn => {
  // ======================================================
  // 認證和訂閱系統
  // ======================================================
  const { currentUser } = useAuth();
  const { showError, showInfo } = useToast();
  const subscription = useSubscription(currentUser);

  // ======================================================
  // 狀態管理
  // ======================================================

  const [isAIPlanSettingsModalOpen, setIsAIPlanSettingsModalOpen] = useState(false);
  const [isAIPlanPreviewModalOpen, setIsAIPlanPreviewModalOpen] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [isApplyingPlan, setIsApplyingPlan] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<{
    date: string;
    timeBoxes: TimeBox[];       // 保留向後相容
    events?: Event[];           // 新增 Event 陣列
    scheduleItems?: ScheduleItem[]; // 新增完整排程
    summary?: string;
    pastScheduleItems?: TimeBox[]; // startTime < now 的既有項目（只顯示，不參與 Apply）
  } | null>(null);

  // AbortController 管理
  const abortControllerRef = useRef<AbortController | null>(null);

  // ======================================================
  // 清理邏輯
  // ======================================================

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // ======================================================
  // 取消生成邏輯
  // ======================================================

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGeneratingPlan(false);
    setPlanError(null);
  }, []);

  // ======================================================
  // Modal 控制
  // ======================================================

  const openSettings = useCallback(() => {
    setIsAIPlanSettingsModalOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsAIPlanSettingsModalOpen(false);
  }, []);

  const closePreview = useCallback(() => {
    if (isGeneratingPlan) {
      cancelGeneration();
    }
    setIsAIPlanPreviewModalOpen(false);
  }, [isGeneratingPlan, cancelGeneration]);

  // ======================================================
  // 主要功能：生成計畫
  // ======================================================

  const generatePlan = useCallback(async (
    date: string,
    latestNote: string,
    existingTimeBoxes?: TimeBox[]
  ) => {
    // 1. 檢查 AI 使用權限
    if (!subscription.canUseAI) {
      const plan = subscription.subscription?.plan || 'free';
      const status = subscription.subscription?.status || 'none';
      // 檢查試用期是否已結束（基於 subscription status）
      const isTrialExpired = (status === 'trialing' && subscription.subscription?.trialEndsAt &&
                              new Date() > subscription.subscription.trialEndsAt);

      if (plan === 'free' && status === 'none') {
        showError(
          'Upgrade Required',
          'Upgrade to Pro for unlimited smart planning features.'
        );
      } else if (isTrialExpired) {
        showError(
          'Trial Ended',
          'Your trial has ended. Upgrade to continue using smart planning features.'
        );
      } else {
        showError('Access Denied', 'Smart planning is not available on your current plan.');
      }
      return;
    }

    // 2. 取消之前的生成
    cancelGeneration();
    abortControllerRef.current = new AbortController();

    // 3. 關閉設定 Modal
    setIsAIPlanSettingsModalOpen(false);

    // 4. 清除錯誤並設定初始計畫
    setPlanError(null);
    setCurrentPlan({
      date,
      timeBoxes: [],
      events: [],
      scheduleItems: [],
      summary: 'One sec — let me timebox your schedule real quick...'
    });

    // 5. 設定生成狀態並開啟預覽 Modal
    setIsGeneratingPlan(true);
    setIsAIPlanPreviewModalOpen(true);

    // 6. 設定超時（90秒）
    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setPlanError('Plan generation timed out. Server may be busy, please try again later.');
        setIsGeneratingPlan(false);
      }
    }, 90000);

    try {

      const userId = currentUser?.uid || '';
      const lastSummary = PlanningSessionManager.getLastSummary(userId, date);

      const plan = await generateDailyPlan(
        date,
        latestNote,
        lastSummary,
        abortControllerRef.current.signal,
        existingTimeBoxes
      );

      // 8. 檢查是否已被取消，防止覆蓋超時狀態
      if (abortControllerRef.current?.signal.aborted) {
        return; // 如果已經取消，忽略回應
      }

      // 9. 設定計畫
      if (plan) {
        // 若是今天的計畫，從 IndexedDB 讀取已開始（startTime < now）的既有 timebox
        let pastScheduleItems: TimeBox[] = [];
        const now = new Date();
        const today = formatDateToString(now);
        if (plan.date === today && currentUser) {
          try {
            const todayTimeBoxes = await getTimeBoxesByDate(currentUser.uid, now);
            pastScheduleItems = todayTimeBoxes
              .filter(tb => tb.status !== 'cancelled' && toDateObject(tb.startTime) < now)
              .sort((a, b) => toDateObject(a.startTime).getTime() - toDateObject(b.startTime).getTime());
          } catch (e) {
            logger.error('讀取既有 timebox 失敗:', e);
          }
        }

        if (plan.summary && userId) {
          PlanningSessionManager.setLastSummary(userId, date, plan.summary);
        }

        setCurrentPlan({
          date: plan.date,
          timeBoxes: plan.timeBoxes,
          events: plan.events || [],
          scheduleItems: plan.scheduleItems || plan.timeBoxes,
          summary: plan.summary,
          pastScheduleItems,
        });

      }

    } catch (error) {
      // 錯誤處理
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          // 用戶取消，不顯示錯誤
        } else {
          logger.error('生成計畫錯誤:', error);
          setPlanError(error.message || 'An error occurred while generating plan, please try again later');
        }
      } else {
        logger.error('未知錯誤:', error);
        setPlanError('An unknown error occurred while generating plan, please try again later');
      }
    } finally {
      // 確保在所有情況下都清除超時
      clearTimeout(timeoutId);
      setIsGeneratingPlan(false);
    }
  }, [subscription.canUseAI, showError, cancelGeneration, currentUser]);

  // ======================================================
  // 主要功能：重新生成計畫（Fine-tune）
  // ======================================================

  const regeneratePlan = useCallback(async (adjustmentText?: string) => {
    if (!subscription.canUseAI) {
      const plan = subscription.subscription?.plan || 'free';
      const status = subscription.subscription?.status || 'none';
      const isTrialExpired = (status === 'trialing' && subscription.subscription?.trialEndsAt &&
                              new Date() > subscription.subscription.trialEndsAt);

      if (plan === 'free' && status === 'none') {
        showError('Upgrade Required', 'Upgrade to Pro for unlimited smart planning features.');
      } else if (isTrialExpired) {
        showError('Trial Ended', 'Your trial has ended. Upgrade to continue using smart planning features.');
      }
      return;
    }

    if (!adjustmentText || !adjustmentText.trim()) return;
    if (!currentPlan) return;

    try {
      const userId = currentUser?.uid || '';
      PlanningSessionManager.setLatestNote(userId, currentPlan.date, adjustmentText.trim());

      await generatePlan(
        currentPlan.date,
        adjustmentText.trim(),
        currentPlan.timeBoxes
      );
    } catch (error) {
      logger.error('Fine-tune 計畫失敗:', error);
    }
  }, [subscription.canUseAI, showError, currentPlan, currentUser, generatePlan]);

  // ======================================================
  // 主要功能：應用計畫
  // ======================================================

  const applyPlan = useCallback(async (
    scheduleItems: ScheduleItem[],
    onSuccess?: () => void
  ) => {
    if (!currentPlan) return;
    if (isGeneratingPlan) return;

    try {
      setIsApplyingPlan(true);

      const userId = currentUser?.uid || '';
      const latestNote = PlanningSessionManager.getLatestNote(userId, currentPlan.date);

      const result = await syncDailySchedule(
        currentPlan.date,
        scheduleItems,
        ['meeting'],
        latestNote ? [latestNote] : []
      );

      if (result.success) {
        PlanningSessionManager.clearSession(userId, currentPlan.date);

        // 關閉預覽 Modal
        setIsAIPlanPreviewModalOpen(false);

        // 執行成功回調
        onSuccess?.();

        // 不顯示成功 Toast：Calendar 已即時更新，結果可見，無需額外確認
      } else {
        showError('Failed to Apply Plan', result.message || 'Please try again later');
        // 失敗時保留 session 供重試
      }
    } catch (error) {
      logger.error('應用計畫失敗:', error);
      showError('Failed to Apply Plan', error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later');
      // 失敗時保留 session 供重試
    } finally {
      setIsApplyingPlan(false);
    }
  }, [currentPlan, isGeneratingPlan, showError]);

  // ======================================================
  // Day Draft V3 狀態機
  // ======================================================

  const [draftStep, setDraftStep] = useState<DayDraftStep>('IDLE');
  const [draftDate, setDraftDate] = useState<string | null>(null);
  const [toneState, setToneSelectionState] = useState<ToneSelectionState | null>(null);
  const toneStateRef = useRef<ToneSelectionState | null>(null);
  useEffect(() => { toneStateRef.current = toneState; }, [toneState]);
  const draftRequestIdRef = useRef<number>(0);
  const [skeletonState, setSkeletonState] = useState<SkeletonState | null>(null);
  const [taskState, setTaskRecommendState] = useState<TaskRecommendState | null>(null);
  const [assignState, setTaskAssignmentState] = useState<TaskAssignmentState | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  const cancelDraft = useCallback(() => {
    draftRequestIdRef.current++;
    setDraftStep('IDLE');
    setDraftDate(null);
    setToneSelectionState(null);
    setSkeletonState(null);
    setTaskRecommendState(null);
    setTaskAssignmentState(null);
    setDraftError(null);
  }, []);

  const startDraft = useCallback(async (date: string) => {
    const requestId = ++draftRequestIdRef.current;

    setDraftError(null);
    setDraftDate(date);
    setToneSelectionState(null);
    setSkeletonState(null);
    setTaskRecommendState(null);
    setTaskAssignmentState(null);
    setDraftStep('TONE_LOADING');

    try {
      const result = await apiGenerateToneOptions(date);
      if (requestId !== draftRequestIdRef.current) return;
      setToneSelectionState({ summary: result.summary, options: result.options });
      setDraftStep('TONE_READY');
    } catch (error) {
      if (requestId !== draftRequestIdRef.current) return;
      logger.error('startDraft 失敗:', error);
      setDraftError('Failed to load tone options. Please try again.');
      setDraftStep('IDLE');
    }
  }, []);

  const selectTone = useCallback(async (intention: string) => {
    if (!draftDate) return;
    logger.info('DayDraft', 'selectTone 開始', { draftDate });
    setDraftError(null);
    setSkeletonState(null);
    setDraftStep('SKELETON_GENERATING');

    try {
      const userId = currentUser?.uid || '';
      const toneSummary = toneStateRef.current?.summary;
      const [result, rawEvents] = await Promise.all([
        apiGenerateSkeleton(draftDate, intention, undefined, undefined, undefined, toneSummary),
        getEventsByDateLocal(userId, new Date(draftDate + 'T12:00:00')).catch(() => []),
      ]);

      logger.info('DayDraft', 'generateSkeleton 完成，設定 SKELETON_READY');
      PlanningSessionManager.setTone(userId, draftDate, intention, toneStateRef.current?.options);
      PlanningSessionManager.setSkeleton(userId, draftDate, result.timeBlocks);

      const alldayEvents: SkeletonAlldayEvent[] = rawEvents
        .filter(e => e.timeType === 'allday')
        .map(e => ({ title: e.title, category: e.category }));

      const rawTimedEvents = rawEvents.filter(e => e.timeType !== 'allday');

      setSkeletonState({
        timeBlocks: result.timeBlocks,
        intention,
        timedEvents: result.events ?? [],
        alldayEvents,
        rawTimedEvents,
        instructionSummary: result.instructionSummary ?? '',
      });
      setDraftStep('SKELETON_READY');
    } catch (error) {
      logger.error('DayDraft', 'selectTone 失敗:', error);
      setDraftError('Failed to generate skeleton. Please try again.');
      setDraftStep('TONE_READY');
    }
  }, [draftDate, currentUser]);

  const fineTuneSkeleton = useCallback(async (instruction: string) => {
    if (!draftDate || !skeletonState) return;
    setDraftError(null);
    setDraftStep('SKELETON_GENERATING');

    try {
      const result = await apiGenerateSkeleton(
        draftDate,
        skeletonState.intention,
        skeletonState.timeBlocks,
        instruction,
        skeletonState.instructionSummary,
      );
      const userId = currentUser?.uid || '';
      PlanningSessionManager.setSkeleton(userId, draftDate, result.timeBlocks);
      setSkeletonState(prev => prev ? {
        ...prev,
        timeBlocks: result.timeBlocks,
        timedEvents: result.events ?? prev.timedEvents,
        instructionSummary: result.instructionSummary || prev.instructionSummary,
      } : null);
      setDraftStep('SKELETON_READY');
    } catch (error) {
      logger.error('fineTuneSkeleton 失敗:', error);
      setDraftError('Failed to adjust skeleton. Please try again.');
      setDraftStep('SKELETON_READY');
    }
  }, [draftDate, skeletonState, currentUser]);

  const finishSkeleton = useCallback(async (onSuccess?: () => void) => {
    if (!draftDate || !skeletonState) return;
    setDraftError(null);
    setDraftStep('APPLYING');

    try {
      const userId = currentUser?.uid || '';
      const dateBase = parseISO(draftDate);
      const timeBoxItems: ScheduleItem[] = skeletonState.timeBlocks.map(tb => ({
        id: '',
        itemType: 'timebox' as const,
        userId,
        title: tb.title,
        startTime: parse(tb.startTime, 'HH:mm', dateBase),
        endTime: parse(tb.endTime, 'HH:mm', dateBase),
        category: tb.category as TimeBox['category'],
        status: 'normal' as const,
        createdAt: new Date(),
        source: { type: 'ai-generated' },
      }));

      const eventItems: ScheduleItem[] = skeletonState.rawTimedEvents.map(e => ({
        ...e,
        itemType: 'event' as const,
      }));

      const result = await syncDailySchedule(draftDate, [...timeBoxItems, ...eventItems], ['meeting']);
      if (!result.success) {
        setDraftError('Failed to apply skeleton. Please try again.');
        setDraftStep('SKELETON_READY');
        return;
      }

      PlanningSessionManager.clearSession(userId, draftDate);
      showInfo('Schedule applied', 'Your calendar is updating…');
      cancelDraft();
      onSuccess?.();
    } catch (error) {
      logger.error('finishSkeleton 失敗:', error);
      setDraftError('Failed to apply skeleton. Please try again.');
      setDraftStep('SKELETON_READY');
    }
  }, [draftDate, skeletonState, currentUser, cancelDraft, showInfo]);

  const startDraftTaskRecommend = useCallback(async () => {
    if (!draftDate || !skeletonState) return;
    setDraftError(null);
    setTaskRecommendState(null);
    setDraftStep('TASK_RECOMMENDING');

    try {
      const result = await apiRecommendTasks(
        draftDate,
        skeletonState.intention,
        skeletonState.timeBlocks
      );
      setTaskRecommendState({ recommendations: result.recommendations, tasks: result.tasks });
      setDraftStep('TASK_READY');
    } catch (error) {
      logger.error('startDraftTaskRecommend 失敗:', error);
      setDraftError('Failed to recommend tasks. Please try again.');
      setDraftStep('SKELETON_READY');
    }
  }, [draftDate, skeletonState]);

  const assignTasks = useCallback(async (selectedTaskIds: string[]) => {
    if (!draftDate || !skeletonState || !taskState) return;
    setDraftError(null);
    setDraftStep('TASK_ASSIGNING');

    try {
      const timeBoxes: TaskAssignmentTimeBox[] = skeletonState.timeBlocks.map((tb, i) => ({
        id: `skeleton-${i}`,
        title: tb.title,
        startTime: tb.startTime,
        endTime: tb.endTime,
        category: tb.category,
        taskIds: [],
      }));

      const taskDetails = taskState.tasks
        .filter(t => selectedTaskIds.includes(t.id))
        .map(t => ({
          id: t.id,
          title: t.title,
          category: t.category,
          estimatedTime: t.estimatedTime,
        }));

      const result = await apiAssignTasksToTimeBoxes(draftDate, selectedTaskIds, timeBoxes, taskDetails);
      setTaskAssignmentState({ timeBoxes: result.timeBoxes });
      setDraftStep('TASK_READY');
    } catch (error) {
      logger.error('assignTasks 失敗:', error);
      setDraftError('Failed to assign tasks. Please try again.');
      setDraftStep('TASK_READY');
    }
  }, [draftDate, skeletonState, taskState]);

  const applyDraft = useCallback(async (onSuccess?: () => void) => {
    if (!draftDate || !assignState) return;
    setDraftError(null);
    setDraftStep('APPLYING');

    try {
      const userId = currentUser?.uid || '';
      const dateBase = parseISO(draftDate);
      const timeBoxItems: ScheduleItem[] = assignState.timeBoxes.map(tb => ({
        id: '',
        itemType: 'timebox' as const,
        userId,
        title: tb.title,
        startTime: parse(tb.startTime, 'HH:mm', dateBase),
        endTime: parse(tb.endTime, 'HH:mm', dateBase),
        category: tb.category as TimeBox['category'],
        status: 'normal' as const,
        taskIds: tb.taskIds,
        createdAt: new Date(),
        source: { type: 'ai-generated' },
      }));

      const eventItems: ScheduleItem[] = (skeletonState?.rawTimedEvents ?? []).map(e => ({
        ...e,
        itemType: 'event' as const,
      }));

      const result = await syncDailySchedule(draftDate, [...timeBoxItems, ...eventItems], ['meeting']);
      if (!result.success) {
        setDraftError('Failed to apply plan. Please try again.');
        setDraftStep('TASK_READY');
        return;
      }

      PlanningSessionManager.clearSession(userId, draftDate);
      showInfo('Schedule applied', 'Your calendar is updating…');
      cancelDraft();
      onSuccess?.();
    } catch (error) {
      logger.error('applyDraft 失敗:', error);
      setDraftError('Failed to apply plan. Please try again.');
      setDraftStep('TASK_READY');
    }
  }, [draftDate, assignState, skeletonState, currentUser, cancelDraft, showInfo]);

  const goBackToTone = useCallback(() => {
    setSkeletonState(null);
    setDraftError(null);
    setDraftStep('TONE_READY');
  }, []);

  const goBackToSkeleton = useCallback(() => {
    setTaskRecommendState(null);
    setTaskAssignmentState(null);
    setDraftError(null);
    setDraftStep('SKELETON_READY');
  }, []);

  // ======================================================
  // 返回接口
  // ======================================================

  return {
    // 狀態（舊版）
    isAIPlanSettingsModalOpen,
    isAIPlanPreviewModalOpen,
    isGeneratingPlan,
    isApplyingPlan,
    planError,
    currentPlan,

    // Modal 控制（舊版）
    openSettings,
    closeSettings,
    closePreview,

    // 主要功能（舊版）
    generatePlan,
    regeneratePlan,
    applyPlan,
    cancelGeneration,

    // Day Draft V3
    draftStep,
    draftDate,
    toneState,
    skeletonState,
    taskState,
    assignState,
    draftError,
    startDraft,
    selectTone,
    finishSkeleton,
    startDraftTaskRecommend,
    assignTasks,
    applyDraft,
    goBackToTone,
    goBackToSkeleton,
    cancelDraft,
    fineTuneSkeleton,
  };
};
